import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { UserService } from '@src/core/services';
import { ViewMode, UserUI } from '@src/models';
import { PermissionService } from '@src/app/modules/organisations';
import {
  PermissionService as UserPermissionService,
  UsersByOrganisationStoreService,
} from '@src/app/modules/user-info';

@Component({
  selector: 'app-organisation-personal',
  templateUrl: './organisation-personal.component.html',
  styleUrls: ['./organisation-personal.component.scss', '../styles/organisation-content.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganisationPersonalComponent extends BaseResizableComponent implements OnChanges {
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly userPermissionService = inject(UserPermissionService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly usersByOrganisationStore = inject(UsersByOrganisationStoreService);

  @Input() title?: string;
  @Input() organisationId?: string | null;
  @Input() selectedUserId?: string;
  @Output() selectedUserIdChange = new EventEmitter<string>();

  users?: UserUI[];

  allowEditing$ = this.permissionService.allowUserEditing$;

  userInfoMode: ViewMode = 'view';
  loading = false;

  private readonly confirmDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.excludeHeader'),
      size: 's',
      closeable: false,
    },
  );

  get showFullPage(): boolean {
    return !this.selectedUserId && this.userInfoMode !== 'create';
  }

  get allowExcluding(): boolean {
    return (
      this.userPermissionService.allowExcluding$.value && this.selectedUserId !== this.userService.authUser$.value?.id
    );
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.usersByOrganisationStore.data$.subscribe(() => {
      if (this.organisationId) {
        this.users = this.usersByOrganisationStore.getData(this.organisationId);
        this.cdr.markForCheck();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.organisationId) {
      this.getData();
    }
  }

  changeSelectedUserId(userId?: string) {
    this.selectedUserId = userId;
    this.selectedUserIdChange.emit(userId);
  }

  onClickAddButton(): void {
    this.changeSelectedUserId();
    this.userInfoMode = 'create';
    this.cdr.detectChanges();
  }

  onClickReturnButton(): void {
    this.changeSelectedUserId();
    this.cdr.detectChanges();
  }

  onChangeSelectedUserId(userId?: string): void {
    this.changeSelectedUserId(userId);
    this.userInfoMode = 'view';
    this.cdr.detectChanges(); // TODO: not work change edit => view
  }

  onCancelEditingUser(): void {
    this.userInfoMode = 'view';
  }

  onSaveEditingUser(): void {
    this.userInfoMode = 'view';
    this.getData();
  }

  onClickExcludeButton(user?: UserUI): void {
    const id = user?.id;
    const organisationId = this.organisationId;

    if (!organisationId || !id) return;

    this.confirmDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          this.userService
            .excludeUser(id, organisationId)
            .pipe(takeUntil(this.destroyed$$))
            .subscribe(() => {
              this.changeSelectedUserId();
              this.getData();
            });
        }
      },
    });
  }

  private getData(): void {
    if (this.organisationId) {
      this.userService.getUsersByOrganisationId(this.organisationId).subscribe();
    }

    this.cdr.markForCheck();
  }
}
